import React, { useEffect, useState } from "react";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";
import axios from "axios";
import "./GereUtilisateurs.css";

interface User {
  Nom_Complet: string;
  Adresse: string;
  Tele: any;
  Password: any;
  EmailAccount?: string;
  TotalCommande: number;
  devis: number;
  nonConfirmes: number;
  Statuts: any;
  id?: number;
}

const GereUtilisateurs: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ Nom_Complet: '', Tele: '', Adresse: '', Password: '', EmailAccount: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  
  const [popup, setPopup] = useState<{
    visible: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({ visible: false, title: "", message: "", type: "success" });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_PHP_APP_URL}/admin/utilisateurs`)
      .then((response) => {
        setUsers(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        setLoading(false);
      });
  }, []);

  const handleDeleteUser = async (userId: number) => {
    try {
      setActionLoading(userId);
      await axios.delete(`${process.env.REACT_APP_PHP_APP_URL}/admin/utilisateurs/${userId}/delete`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setPopup({ visible: true, title: "Succès", message: "Utilisateur supprimé avec succès", type: "success" });
      setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setPopup({ visible: true, title: "Erreur", message: "Erreur lors de la suppression", type: "error" });
      setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (user: User) => {
    setEditUser(user);
    setEditForm({
      Nom_Complet: user.Nom_Complet,
      Tele: String(user.Tele),
      Adresse: user.Adresse ?? '',
      Password: user.Password ?? '',
      EmailAccount: user.EmailAccount ?? '',
    });
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editUser?.id) return;
    setEditLoading(true);
    setEditError('');
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/visiteur/${editUser.id}/update`, {
        Nom_Complet: editForm.Nom_Complet,
        Tele: editForm.Tele,
        Adresse: editForm.Adresse,
        EmailAccount: editForm.EmailAccount,
        ...(editForm.Password.trim() ? { Password: editForm.Password } : {}),
      });
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...editForm } : u));
      setEditUser(null);
      setPopup({ visible: true, title: 'Succès', message: 'Utilisateur mis à jour.', type: 'success' });
      setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
    } catch (err: any) {
      setEditError(err?.response?.data?.message ?? 'Une erreur est survenue.');
    } finally {
      setEditLoading(false);
    }
  };

  const getUserStatus = (user: User) => {
    const total = user.TotalCommande ?? 0;
    const annule = user.Statuts?.["annulé"] ?? 0;
    const valide = user.Statuts?.["confirmé"] ?? 0;

    if (total === 0) return { label: "Aucune activité", class: "gu-status-inactive", icon: "dash-circle" };
    
    const annulePercent = (annule / total) * 100;
    const validePercent = (valide / total) * 100;

    if (validePercent > 70) return { label: "Excellent", class: "gu-status-excellent", icon: "star-fill" };
    if (validePercent > 50) return { label: "Bon", class: "gu-status-bon", icon: "check-circle-fill" };
    if (annulePercent > 50) return { label: "Mauvais", class: "gu-status-mauvais", icon: "x-circle-fill" };
    
    return { label: "Normal", class: "gu-status-normal", icon: "dash-circle-fill" };
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch = u.Nom_Complet.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "Tous" || getUserStatus(u).label === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="gu-page">
      <NewSideBarAdmin />

      {loading ? (
        <div className="gu-loader">
          <div className="gu-loader-inner">
            <div className="gu-spinner"></div>
            <p className="gu-loader-text">Chargement des utilisateurs...</p>
          </div>
        </div>
      ) : (
        <div className="gu-container">
          <div className="gu-header">
            <h1 className="gu-title">
              <i className="bi bi-people-fill"></i>
              Gestion des Utilisateurs
            </h1>
            <p className="gu-subtitle">
              Gérez et surveillez tous vos utilisateurs en un coup d'œil
            </p>
          </div>

          <div className="gu-search-card">
            <div className="gu-search-header">
              <h3 className="gu-search-title">
                <i className="bi bi-search"></i>
                Rechercher un utilisateur
                <span className="gu-count">{filteredUsers.length}</span>
              </h3>
              <p className="gu-search-sub">Recherchez par nom, email ou téléphone</p>
            </div>
            
            <div className="gu-search-row">
              <div className="gu-search-wrap">
                <i className="bi bi-search gu-search-icon"></i>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="gu-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="gu-select"
              >
                <option value="Tous">Tous les statuts</option>
                <option value="Excellent">Excellent</option>
                <option value="Bon">Bon</option>
                <option value="Normal">Normal</option>
                <option value="Mauvais">Mauvais</option>
                <option value="Aucune activité">Aucune activité</option>
              </select>
            </div>
          </div>

          {!isMobile ? (
            <div className="gu-table-card">
              <table className="gu-table">
                <thead>
                  <tr>
                    <th><i className="bi bi-person"></i> Nom</th>
                    <th><i className="bi bi-telephone"></i> Téléphone</th>
                    <th><i className="bi bi-key"></i> Mot de passe</th>
                    <th><i className="bi bi-cart"></i> Commandes</th>
                    <th><i className="bi bi-check-circle"></i> Confirmé</th>
                    <th><i className="bi bi-x-circle"></i> Annulé</th>
                    <th><i className="bi bi-clock"></i> En cours</th>
                    <th><i className="bi bi-flag"></i> Status</th>
                    <th><i className="bi bi-gear"></i> Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => {
                      const status = getUserStatus(user);
                      return (
                        <tr key={index}>
                          <td>{user.Nom_Complet}</td>
                          <td>{user.Tele}</td>
                          <td>{user.Password}</td>
                          <td><span className="gu-badge gu-badge-blue">{user.TotalCommande}</span></td>
                          <td><span className="gu-badge gu-badge-green">{user.Statuts?.["confirmé"] ?? 0}</span></td>
                          <td><span className="gu-badge gu-badge-red">{user.Statuts?.["annulé"] ?? 0}</span></td>
                          <td><span className="gu-badge gu-badge-yellow">{user.Statuts?.["en cours"] ?? 0}</span></td>
                          <td>
                            <span className={`gu-status ${status.class}`}>
                              <i className={`bi bi-${status.icon}`}></i> {status.label}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="gu-action-btn gu-action-btn-edit"
                                onClick={() => openEditModal(user)}
                                title="Modifier"
                              >
                                <i className="bi bi-pencil-fill" />
                              </button>
                              <button
                                className="gu-action-btn gu-action-btn-delete"
                                onClick={() => handleDeleteUser(user.id || index)}
                                title="Supprimer"
                                disabled={actionLoading === (user.id || index)}
                              >
                                {actionLoading === (user.id || index) ? (
                                  <i className="bi bi-hourglass-split"></i>
                                ) : (
                                  <i className="bi bi-trash-fill" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9}>
                        <div className="gu-empty">
                          <i className="bi bi-inbox gu-empty-icon"></i>
                          <p className="gu-empty-text">Aucun utilisateur trouvé</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="gu-mobile-list">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => {
                  const status = getUserStatus(user);
                  return (
                    <div key={index} className="gu-mobile-card">
                      <div className="gu-mobile-card-header">
                        <h3 className="gu-mobile-name">{user.Nom_Complet}</h3>
                        <div className="gu-mobile-actions">
                          <button
                            className="gu-action-btn gu-action-btn-edit"
                            onClick={() => openEditModal(user)}
                          >
                            <i className="bi bi-pencil-fill" />
                          </button>
                          <button
                            className="gu-action-btn gu-action-btn-delete"
                            onClick={() => handleDeleteUser(user.id || index)}
                            disabled={actionLoading === (user.id || index)}
                          >
                            {actionLoading === (user.id || index) ? (
                              <i className="bi bi-hourglass-split"></i>
                            ) : (
                              <i className="bi bi-trash-fill" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="gu-mobile-info">
                        <span className="gu-mobile-label"><i className="bi bi-telephone"></i> Téléphone</span>
                        <span className="gu-mobile-val">{user.Tele}</span>
                      </div>

                      <div className="gu-mobile-stats">
                        <div className="gu-mobile-stat">
                          <div className="gu-mobile-stat-label">Total</div>
                          <span className="gu-badge gu-badge-blue">{user.TotalCommande}</span>
                        </div>
                        <div className="gu-mobile-stat">
                          <div className="gu-mobile-stat-label">Confirmé</div>
                          <span className="gu-badge gu-badge-green">{user.Statuts?.["confirmé"] ?? 0}</span>
                        </div>
                        <div className="gu-mobile-stat">
                          <div className="gu-mobile-stat-label">Annulé</div>
                          <span className="gu-badge gu-badge-red">{user.Statuts?.["annulé"] ?? 0}</span>
                        </div>
                      </div>

                      <div className="gu-mobile-info">
                        <span className="gu-mobile-label"><i className="bi bi-clock"></i> En cours</span>
                        <span className="gu-badge gu-badge-yellow">{user.Statuts?.["en cours"] ?? 0}</span>
                      </div>

                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--gu-border)', display: 'flex', justifyContent: 'flex-end' }}>
                        <span className={`gu-status ${status.class}`}>
                          <i className={`bi bi-${status.icon}`}></i> {status.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="gu-empty">
                  <i className="bi bi-inbox gu-empty-icon"></i>
                  <p className="gu-empty-text">Aucun utilisateur trouvé</p>
                </div>
              )}
            </div>
          )}

          {popup.visible && (
            <div className={`gu-popup ${popup.type === 'success' ? 'gu-popup-success' : 'gu-popup-error'}`}>
              <h5 className="gu-popup-title">
                <i className={`bi ${popup.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}></i>
                {popup.title}
              </h5>
              <p className="gu-popup-msg">{popup.message}</p>
            </div>
          )}
        </div>
      )}

      {editUser && (
        <div className="gu-modal-overlay">
          <div className="gu-modal">
            <div className="gu-modal-header">
              <h3 className="gu-modal-title">
                <i className="bi bi-pencil-square"></i> Modifier visiteur
              </h3>
              <button className="gu-modal-close" onClick={() => setEditUser(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="gu-modal-body">
              {editError && (
                <div className="gu-modal-error">
                  <i className="bi bi-exclamation-triangle-fill"></i> {editError}
                </div>
              )}

              {[['Nom complet', 'Nom_Complet', 'text'], ['Email', 'EmailAccount', 'email'], ['Téléphone', 'Tele', 'tel'], ['Adresse', 'Adresse', 'text'], ['Mot de passe', 'Password', 'text']].map(([label, field, type]) => (
                <div key={field} className="gu-modal-group">
                  <label className="gu-modal-label">{label}</label>
                  <input
                    type={type}
                    value={(editForm as any)[field]}
                    onChange={e => setEditForm(prev => ({ ...prev, [field]: e.target.value }))}
                    className="gu-modal-input"
                  />
                </div>
              ))}
            </div>

            <div className="gu-modal-footer">
              <button className="gu-btn-cancel" onClick={() => setEditUser(null)}>
                Annuler
              </button>
              <button className="gu-btn-save" onClick={handleEditSave} disabled={editLoading}>
                {editLoading ? (
                  <>
                    <i className="bi bi-hourglass-split"></i> Enregistrement...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-circle"></i> Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GereUtilisateurs;